const UNITS = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];

export function formatBytes(bytes: number, digits = 1): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '-';
  if (bytes === 0) return '0 B';
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), UNITS.length - 1);
  const value = bytes / 1024 ** i;
  return `${value.toFixed(i === 0 ? 0 : digits)} ${UNITS[i]}`;
}

export function formatSpeed(bytesPerSecond: number): string {
  return `${formatBytes(bytesPerSecond, 1)}/s`;
}

const rtf = new Intl.RelativeTimeFormat('ko', { numeric: 'auto' });

export function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '-';
  const diff = then - Date.now();
  const abs = Math.abs(diff);

  const minute = 60_000;
  const hour = 60 * minute;
  const day = 24 * hour;

  if (abs < minute) return '방금';
  if (abs < hour) return rtf.format(Math.round(diff / minute), 'minute');
  if (abs < day) return rtf.format(Math.round(diff / hour), 'hour');
  if (abs < 7 * day) return rtf.format(Math.round(diff / day), 'day');
  return formatDate(iso);
}

const dateFmt = new Intl.DateTimeFormat('ko-KR', {
  year: 'numeric', month: '2-digit', day: '2-digit',
});
const dateTimeFmt = new Intl.DateTimeFormat('ko-KR', {
  year: 'numeric', month: '2-digit', day: '2-digit',
  hour: '2-digit', minute: '2-digit',
});

export function formatDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '-' : dateFmt.format(d);
}

export function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '-' : dateTimeFmt.format(d);
}

export function extensionOf(name: string): string {
  const i = name.lastIndexOf('.');
  return i <= 0 ? '' : name.slice(i + 1).toLowerCase();
}

export function parentPath(path: string): string {
  const i = path.lastIndexOf('/');
  return i <= 0 ? '' : path.slice(0, i);
}

export function baseName(path: string): string {
  const i = path.lastIndexOf('/');
  return i < 0 ? path : path.slice(i + 1);
}

export function joinPath(parent: string, name: string): string {
  return parent === '' ? `/${name}` : `${parent}/${name}`;
}

export type PreviewKind = 'image' | 'pdf' | 'text' | 'video' | 'audio' | 'none';

const TEXT_EXT = new Set([
  'txt', 'md', 'csv', 'log', 'json', 'xml', 'yaml', 'yml', 'css', 'js', 'ts', 'tsx', 'jsx',
  'py', 'go', 'java', 'c', 'cpp', 'cs', 'rb', 'php', 'sh', 'sql', 'ini', 'conf', 'env',
]);

/** SVG/HTML 은 저장형 XSS 위험 때문에 렌더링하지 않는다 (서버 정책과 동일). */
export function previewKindOf(name: string, mime: string | null): PreviewKind {
  const ext = extensionOf(name);
  if (ext === 'svg' || ext === 'html' || ext === 'htm') return 'none';
  if (mime?.startsWith('image/')) return 'image';
  if (mime === 'application/pdf' || ext === 'pdf') return 'pdf';
  if (mime?.startsWith('video/')) return 'video';
  if (mime?.startsWith('audio/')) return 'audio';
  if (TEXT_EXT.has(ext) || mime?.startsWith('text/')) return 'text';
  return 'none';
}

export function initialsOf(name: string): string {
  const trimmed = name.trim();
  return trimmed ? trimmed.slice(0, 1).toUpperCase() : '?';
}
